'use client'

import { usePathname } from 'next/navigation'
import { useTranslation } from 'react-i18next'
import { CHROME_STRINGS } from './chrome-strings'
import { getPathLocale } from './supported-locales'

/**
 * `t` for the site chrome (header + footer), locale-correct on `/{lang}` routes.
 *
 * THE PROBLEM
 * -----------
 * The header and footer are rendered by the ROOT layout, which sits ABOVE app/[lang]/layout.jsx
 * and therefore outside LocaleI18nProvider. They resolve `useTranslation()` against the shared
 * i18n instance, which is pinned to English during SSR (it has to be — it is a mutable module
 * singleton, so switching its language server-side would race across concurrent requests for
 * different locales; that is exactly why app/i18n/instances.js exists).
 *
 * The result: /es, /fr, /de and every other localized page shipped correctly translated body
 * copy wrapped in an entirely English nav and footer — "Services", "AI Tools", "Shop",
 * "Business / API" — in the server-rendered HTML a crawler sees.
 *
 * WHY NOT JUST USE THE LOCALE INSTANCE
 * ------------------------------------
 * Importing app/i18n/instances.js into the root layout would pull all ten translation.json
 * files (~1.2 MB raw / ~300 KB gzipped) into the shared client bundle for EVERY route. With
 * mobile LCP already failing, that trades an SEO nit for a Core Web Vitals regression.
 *
 * This resolves the ~38 chrome keys from a generated per-locale subset (~14 KB raw for all ten
 * locales) and defers everything else to the normal `t`. `usePathname()` is available during
 * SSR of client components and returns the same value on the first client render, so the
 * chrome server-renders in the right language and hydrates without a mismatch.
 */
export function useChromeT() {
  const { t } = useTranslation()
  const pathname = usePathname()
  const locale = getPathLocale(pathname)

  // No locale prefix → English root routes and the in-place language switcher. Behaviour is
  // completely unchanged there: the shared instance already handles those correctly.
  if (!locale) return t

  const strings = CHROME_STRINGS[locale]
  if (!strings) return t

  // Falls through to the real `t` for anything the generated subset doesn't carry, so the
  // default-value form — t('cookieConsent.settings', 'Cookie settings') — keeps working, as
  // does interpolation. A key added to the chrome without rerunning the generator degrades to
  // the current English behaviour rather than rendering a raw key.
  return (key, ...rest) => {
    const value = strings[key]
    return typeof value === 'string' ? value : t(key, ...rest)
  }
}
